import { LockClosedIcon, TrashIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";

import { LabelConfigurationUpdate, ReleaseLabelConfigurationDialogProps } from "./types";

import { Switch } from "@/components/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetOrganizationRoles } from "@/queries/organizations";

const ReleaseLabelConfigurationDialog = ({
  isOpen,
  onOpenChange,
  label,
  onSave,
  onDelete,
  organizationId,
  readOnly = false,
  isSkillCollection = false,
}: ReleaseLabelConfigurationDialogProps) => {
  const [isProtected, setIsProtected] = useState(false);
  const [approverRoleId, setApproverRoleId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetOrganizationRoles(
    organizationId,
    isOpen && !!organizationId,
  );

  const roles = rolesData?.roles ?? [];
  const hasNoRoles = roles.length === 0;

  useEffect(() => {
    if (label && isOpen) {
      setIsProtected(label.is_protected ?? false);
      setApproverRoleId(label.approver_role_id ?? null);
    }
  }, [label, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmingDelete(false);
    }
  }, [isOpen]);

  const hasChanges =
    isProtected !== (label?.is_protected ?? false) ||
    approverRoleId !== (label?.approver_role_id ?? null);

  const handleSave = async () => {
    if (!label) {
      return;
    }

    setIsSaving(true);
    try {
      const config: LabelConfigurationUpdate = {
        is_protected: isProtected,
        approver_role_id: isProtected ? approverRoleId : null,
      };
      await onSave(label.id, config);
      onOpenChange(false);
    } catch {
      // Error is already handled by the mutation
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!isSaving) {
      onOpenChange(open);
    }
  };

  const handleApproverRoleChange = (value: string) => {
    if (value === "none") {
      setApproverRoleId(null);
    } else {
      setApproverRoleId(Number(value));
    }
  };

  const handleDeleteClick = () => {
    setIsConfirmingDelete(true);
  };

  const handleConfirmDelete = () => {
    if (label) {
      onDelete(label);
      onOpenChange(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmingDelete(false);
  };

  const handleUpgradeClick = () => {
    window.open("https://www.promptlayer.com/pricing", "_blank");
  };

  if (isConfirmingDelete) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Remove Release Label</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to remove the{" "}
              <span className="font-semibold">&quot;{label?.name}&quot;</span> release label? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelDelete} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isSaving}>
              {isSaving ? "Removing..." : "Remove Label"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Configure Release Label</DialogTitle>
          <DialogDescription className="pt-1">
            Manage settings for the <span className="font-semibold">&quot;{label?.name}&quot;</span>{" "}
            release label.
          </DialogDescription>
        </DialogHeader>

        {!isSkillCollection && (
          <div className="flex flex-col gap-6 py-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 flex-col gap-1.5">
                <span className="text-sm font-semibold">Protected Label</span>
                <span className="text-sm leading-relaxed text-muted-foreground">
                  When enabled, changes to this label require approval from authorized users
                </span>
              </div>
              <Switch
                checked={isProtected}
                onChange={setIsProtected}
                size="sm"
                disabled={readOnly}
              />
            </div>

            {isProtected && (
              <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold">Approver Role</label>
                  <span className="text-sm leading-relaxed text-muted-foreground">
                    Select which role can approve changes to this protected label
                  </span>
                </div>

                <Select
                  value={approverRoleId?.toString() ?? "none"}
                  onValueChange={handleApproverRoleChange}
                  disabled={isLoadingRoles || readOnly}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select an approver role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific role required</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasNoRoles && (
                  <div className="flex flex-col gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-primary/10 p-1.5">
                        <LockClosedIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <h4 className="text-sm font-semibold text-foreground">
                          Want role-specific approvals?
                        </h4>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          Upgrade to RBAC to assign specific roles that can approve changes to this
                          label.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleUpgradeClick}
                      variant="blue"
                      className="w-full"
                      size="sm"
                    >
                      Learn About RBAC
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {!readOnly && (
            <Button
              variant="destructiveLink"
              onClick={handleDeleteClick}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete Label
            </Button>
          )}
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {hasChanges ? "Cancel" : "Close"}
            </Button>
            {!readOnly && hasChanges && (
              <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseLabelConfigurationDialog;
